import { normalizeError } from 'core/common/errors';
import { SpanAttributes, SpanName } from 'core/common/observability/entities';
import { ATTRS_ANALYTICS_EVENT_NAME } from 'core/common/observability/entities/SemanticAttributes';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { ErrorMonitoring } from 'core/common/services/errorMonitoring';
import { FacebookEventNames } from '../../entities';
import { FacebookService } from '../../interfaces';
import { FacebookServiceDecorator } from './FacebookServiceDecorator';

export class FacebookServiceWithMonitoring extends FacebookServiceDecorator {
  constructor(
    facebookService: FacebookService,
    private readonly observabilitySystem: ObservabilitySystem,
    private readonly errorMonitoringService: ErrorMonitoring,
    private readonly receiveAdditionalObservabilityAttributes?: () => SpanAttributes,
  ) {
    super(facebookService);
  }

  track(name: FacebookEventNames, orderId: string, params?: Record<string, unknown>) {
    if (!super.isFbqRequired()) {
      try {
        return super.track(name, orderId, params);
      } catch {
        this.errorMonitoringService.captureError('Facebook track failed', {
          tags: {
            error_message: 'Facebook track failed',
            code: 'facebook_track_failed_when_not_required',
          },
        });
        return;
      }
    }

    if (super.isFbqRequired() && !super.isFbqAvailable()) {
      try {
        return super.track(name, orderId, params);
      } catch {
        this.errorMonitoringService.captureError('Facebook track failed', {
          tags: {
            error_message: 'Facebook track failed',
            code: 'facebook_track_failed_when_required_not_available',
          },
        });
        return;
      }
    }

    const span = this.observabilitySystem.startSpan(SpanName.TRACK_ANALYTICS_FACEBOOK);
    const spanAttributes = {
      [ATTRS_ANALYTICS_EVENT_NAME]: name,
      ...(this.receiveAdditionalObservabilityAttributes
        ? this.receiveAdditionalObservabilityAttributes()
        : {}),
    };
    span.setAttributes(spanAttributes);

    try {
      const result = super.track(name, orderId, params);
      span.succeed();
      return result;
    } catch (err) {
      const error = normalizeError(err);
      span.fail(error);
      return;
    }
  }
}
